import React, { useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { PageSkeleton } from '@/components/ui/skeletons';

/**
 * Garde de route admin (défense en profondeur).
 *
 * Réutilise le hook éprouvé useAdminAuth (déjà utilisé par AdminDashboard en
 * prod) : pendant la PREMIÈRE vérification → squelette (PAS de redirection, pour
 * ne jamais éjecter un admin légitime durant le chargement de session) ; une fois
 * confirmé non-authentifié → redirection vers /admin/login.
 *
 * La barrière réelle des DONNÉES reste la RLS Supabase ; ceci empêche juste le
 * montage des écrans admin pour un visiteur non authentifié.
 *
 * UNE FOIS L'ACCÈS ACCORDÉ, ON NE REDÉMONTE PLUS LES ENFANTS.
 *
 * Cette garde renvoyait un squelette dès que `isLoading` repassait à true — ce qui
 * survient à chaque revérification de session, donc à chaque retour sur l'onglet.
 * React démontait alors l'écran admin ouvert et TOUT son état : un devis en cours de
 * saisie disparaissait sans le moindre message, et sans que rien ne soit enregistré.
 *
 * Le hook ne repasse plus en chargement après coup, mais la garde ne s'y fie pas :
 * elle mémorise que l'accès a été accordé. Deux verrous valent mieux qu'un quand le
 * prix d'une régression est le travail en cours de l'utilisateur.
 */
export const RequireAdmin = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAdminAuth();

  const dejaAutorise = useRef(false);
  if (isAuthenticated) dejaAutorise.current = true;

  // Première vérification seulement : on attend sans rien afficher d'autre.
  if (!dejaAutorise.current && isLoading) return <PageSkeleton />;

  // Seule une réponse NÉGATIVE CERTAINE éjecte : plus de session ET plus aucune
  // vérification en cours. Une erreur réseau transitoire, qui remet brièvement
  // `isAuthenticated` à false, laisse l'écran en place.
  if (!isAuthenticated && !isLoading) return <Navigate to="/admin/login" replace />;

  return <>{children}</>;
};

export default RequireAdmin;
